/**
 * PSP/CRE Tool v2.0 - Host Nation Support (HNS) Configuration Constants
 *
 * Purpose: Define HNS support levels, their effect on military medical capacity,
 * and helper functions for validation and modifier computation.
 *
 * HNS in NATO doctrine (AJP-4.10C §5.4, STANAG 2228):
 * The Host Nation provides support to visiting NATO forces under a Host Nation
 * Support Agreement (HNSA). For medical planning this means:
 *   - Civilian hospitals made available to military patients (bed augmentation)
 *   - Civilian ambulances and medical transport available under MOU
 *   - Civilian medical personnel augmenting military Role 3/4 facilities
 *   - Civilian DNBI/accident absorption reducing military medical system load
 *
 * HNS reliability is NOT guaranteed — civilian systems degrade under wartime
 * conditions, staff may not report, facilities may be damaged or overwhelmed
 * by civilian casualties. The reliability modifier captures this uncertainty.
 *
 * Author: Dr. Tatar Radu MD
 * Date: March 10, 2026
 * Version: 1.0.0 — HNS-001: Initial build (Tab2 UI already complete)
 *
 * Sources:
 *   - AJP-4.10C §5.4 — Host Nation Medical Support
 *   - STANAG 2228 — Requirements for Host Nation Support
 *   - Romania–NATO HNS Agreement (2016, unclassified summary)
 *   - AJMedP-1 Annex B — HNS medical planning factors
 */

// ============================================================================
// HNS SUPPORT LEVELS
// Each level represents an agreed HNS package from the host nation.
// civilian_hospital_beds: additional beds available to military patients
// civilian_ambulances: additional civilian ambulances under military tasking
// dnbi_absorption: fraction of military non-battle casualties absorbed by
//   civilian medical system (reduces military DNBI load)
// bed_augmentation_percent: R3 bed capacity increase as fraction of organic
// reliability: probability that agreed HNS actually delivers as planned
//   (wartime degradation, staff non-availability, facility damage)
//
// Source basis:
//   NONE/MINIMAL: standard peacetime / contested environment baseline
//   STANDARD: typical HNSA for exercise or limited conflict (Romania–NATO 2016)
//   ROBUST/FULL: full wartime HNS including civilian hospital requisition
// ============================================================================

export const HNS_SUPPORT_LEVELS = {
  NONE: {
    label: 'None',
    icon: '○',
    description: 'No host nation support — military medical system self-sufficient',
    civilian_hospital_beds: 0,
    civilian_ambulances: 0,
    dnbi_absorption: 0.00,
    bed_augmentation_percent: 0.00,
    reliability: 1.00  // No dependency = no reliability risk
  },
  MINIMAL: {
    label: 'Minimal',
    icon: '◔',
    description: 'Limited HNS — some liaison, minimal resource sharing. Typical for contested or degraded host nation environment.',
    civilian_hospital_beds: 50,
    civilian_ambulances: 4,
    dnbi_absorption: 0.05,
    bed_augmentation_percent: 0.10,
    reliability: 0.70
  },
  STANDARD: {
    label: 'Standard',
    icon: '◑',
    description: 'Standard HNSA package — designated civilian hospitals, ambulance MOU, liaison officers. Typical NATO exercise / limited conflict baseline.',
    civilian_hospital_beds: 150,
    civilian_ambulances: 10,
    dnbi_absorption: 0.15,
    bed_augmentation_percent: 0.25,
    reliability: 0.85
  },
  ROBUST: {
    label: 'Robust',
    icon: '◕',
    description: 'Robust HNS — multiple hospital agreements, dedicated civilian ambulance pool, civilian medical staff augmentation. Cooperative host nation in stable rear area.',
    civilian_hospital_beds: 300,
    civilian_ambulances: 20,
    dnbi_absorption: 0.25,
    bed_augmentation_percent: 0.40,
    reliability: 0.90
  },
  FULL: {
    label: 'Full',
    icon: '●',
    description: 'Full HNS — host nation civilian medical system fully integrated. Civilian hospitals requisitioned, national ambulance service under military tasking. Requires stable host nation and comprehensive HNSA.',
    civilian_hospital_beds: 500,
    civilian_ambulances: 35,
    dnbi_absorption: 0.35,
    bed_augmentation_percent: 0.60,
    reliability: 0.95
  }
};

// ============================================================================
// DEFAULT HNS CONFIG
// Matches the shape Tab2 reads from scenario.operation_parameters.hns_config.
// HNS disabled by default — planner must actively enable and configure.
// ============================================================================

export const DEFAULT_HNS_CONFIG = {
  enabled: false,
  support_level: 'NONE',
  hospitals: [],           // Future: named hospital facilities (Tab3 integration)
  ambulances: {            // Future: per-type civilian ambulance count
    als: 0,
    bls: 0,
    transport: 0
  }
};

// ============================================================================
// NATO EXERCISE HNS CONFIG
// Standard preset for NATO exercise planning (STANAG 2228 exercise baseline).
// Applied when user clicks "Apply NATO Exercise" in Tab2.
// Support level STANDARD reflects typical exercise HNS package.
// ============================================================================

export const NATO_EXERCISE_HNS_CONFIG = {
  enabled: true,
  support_level: 'STANDARD',
  hospitals: [],
  ambulances: {
    als: 4,
    bls: 6,
    transport: 0
  }
};

// ============================================================================
// getHNSModifiers(hnsConfig)
// Computes the effective modifier set from the current HNS configuration.
// Called by Tab2 buildUpdates() and stored in scenario.operation_parameters
// .hns_config.modifiers for use by casualtyCalculator.js.
//
// Returns:
//   bed_augmentation_absolute: additional beds (reliability-adjusted)
//   bed_augmentation_percent:  fraction of R3 organic beds added
//   dnbi_absorption:           fraction of DNBI load absorbed by HN system
//   civilian_ambulances:       reliability-adjusted ambulance count
//   reliability:               HNS delivery reliability factor (0–1)
//   effective_level:           the level key that was applied
//   is_active:                 true if HNS is enabled and level is not NONE
// ============================================================================

export function getHNSModifiers(hnsConfig) {
  if (!hnsConfig || !hnsConfig.enabled || hnsConfig.support_level === 'NONE') {
    return {
      bed_augmentation_absolute: 0,
      bed_augmentation_percent: 0.00,
      dnbi_absorption: 0.00,
      civilian_ambulances: 0,
      reliability: 1.00,
      effective_level: 'NONE',
      is_active: false
    };
  }

  const level = HNS_SUPPORT_LEVELS[hnsConfig.support_level];
  if (!level) {
    throw new Error(
      `[hnsConfig] getHNSModifiers: unknown support_level "${hnsConfig.support_level}". ` +
      `Valid levels: ${Object.keys(HNS_SUPPORT_LEVELS).join(', ')}`
    );
  }

  const reliability = level.reliability;

  return {
    // Reliability-adjusted values — what military planners can actually count on
    bed_augmentation_absolute: Math.floor(level.civilian_hospital_beds * reliability),
    bed_augmentation_percent:  level.bed_augmentation_percent * reliability,
    dnbi_absorption:           level.dnbi_absorption * reliability,
    civilian_ambulances:       Math.floor(level.civilian_ambulances * reliability),
    // Raw values — for display / transparency in Tab5
    nominal_beds:              level.civilian_hospital_beds,
    nominal_ambulances:        level.civilian_ambulances,
    reliability,
    effective_level:           hnsConfig.support_level,
    is_active:                 true
  };
}

// ============================================================================
// validateHNSConfig(hnsConfig)
// Called by Tab2 validation pass before saving.
// Returns { valid: bool, errors: string[], warnings: string[] }
// ============================================================================

export function validateHNSConfig(hnsConfig) {
  const errors = [];
  const warnings = [];

  if (!hnsConfig) {
    return { valid: false, errors: ['HNS configuration object is missing'], warnings: [] };
  }

  // Validate support_level is a known key
  if (!HNS_SUPPORT_LEVELS[hnsConfig.support_level]) {
    errors.push(
      `Unknown HNS support level "${hnsConfig.support_level}". ` +
      `Valid: ${Object.keys(HNS_SUPPORT_LEVELS).join(', ')}`
    );
  }

  // Warn on FULL level — requires comprehensive HNSA documentation
  if (hnsConfig.support_level === 'FULL') {
    warnings.push(
      'FULL HNS requires a signed, comprehensive Host Nation Support Agreement (HNSA). ' +
      'Verify agreement covers medical facilities and ambulance tasking before planning.'
    );
  }

  // Warn on ROBUST/FULL when planning a contested operation
  // (reliability degrades further in contested environments — planner awareness)
  if (['ROBUST', 'FULL'].includes(hnsConfig.support_level)) {
    warnings.push(
      'High HNS levels assume a stable rear area. In contested environments, ' +
      'effective HNS delivery may be lower than the reliability factor indicates.'
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const hnsConfigConstants = {
  HNS_SUPPORT_LEVELS,
  DEFAULT_HNS_CONFIG,
  NATO_EXERCISE_HNS_CONFIG,
  getHNSModifiers,
  validateHNSConfig
};

export default hnsConfigConstants;
